// @flow
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Redirect, useLocation  } from 'react-router-dom';

// IMPORTING CSS
import 'assets/css/shared.css';
import './styles.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';

// IMPORT IMAGES
import bgImage from 'assets/images/background.png';
import gsIcon from 'assets/images/logo-2x.png';

// IMPORT IS
import { isValidPassword } from 'utils/validators';
import DownloadAppPopUp from 'components/shared/DownloadAppPopUp/DownloadAppPopUp';
import ErrorPopUp from 'components/shared/ErrorPopUp/ErrorPopUp';

import LoadingButton from 'components/shared/LoadingButton/LoadingButton';
import { useTranslation } from 'react-i18next';

type Props = {
  changePassword: (hash: string, newPassword: string) => Promise<any>,
  ...
}

const ChangePasswordView = (props: Props) => {
  const { register, handleSubmit, errors } = useForm({ mode: 'onTouched' });

  const stateFromForgotPage = useLocation().state;

  if (!stateFromForgotPage) {
    const landingsURL = `${process.env.REACT_APP_LANDINGS_URL}?linkResetPasswordExpired=1`;
    window.location = landingsURL;
  }

  const [pageState, updatePageState]: [any, ((any => any) | any) => void] = useState({ hash: useLocation().state.hash });

  const [showSuccessPopUp, setShowSuccessPopUp] = useState(false);

  const [errorPopUp, updateErrorPopUp]: [any, ((any => any) | any) => void] = useState({ show: false });

  const [loading, setLoading] = useState(false);

  const passwordHash = pageState.hash;

  const { t } = useTranslation();

  const onChangeInput = (event) => {
    updatePageState({
      ...pageState,
      [event.target.name]: event.target.value,
    });
  };

  const passwordsMatches = () => {
    let isValid = false;

    if (pageState.password && pageState.passwordRepeat) {
      isValid = (pageState.password === pageState.passwordRepeat);
    }

    return isValid;
  };

  const getErrorMessage = () => {
    let errorMessage = null;

    if (errors.password?.type === 'required' || errors.passwordRepeat?.type === 'required') {
      errorMessage = t('reset_password_error_empty');
    } else if (errors.password?.type === 'format' || errors.passwordRepeat?.type === 'format') {
      errorMessage = t('reset_password_error_length');
    } else if (errors.passwordRepeat?.type === 'match') {
      errorMessage = t('reset_password_error_match');
    }

    return errorMessage;
  }

  const showErrors = () => {
    var errorMessage = getErrorMessage();

    return (
      <div className="error-box">
        {errorMessage != null && <FontAwesomeIcon icon={faExclamationTriangle} />}
        <div className="error-text">
          {errorMessage}
        </div>
      </div>
    );
  };

  const onSubmit = () => {
    setLoading(true);

    props.changePassword(passwordHash, pageState.password)
      .then(() => {
        window.location.href = '/successpassword';
      })
      .catch(response => {
        const errorState = {
          ...errorPopUp,
          show: true,
          title: ""
        };

        errorState.btnBackText = null;

        if(response.status == null || response.status == undefined)
          updateErrorPopUp(errorState);

        if (response.status === 409) {
          errorState.description = t('reset_password_error_expired');
          updateErrorPopUp(errorState);
        }

        if(response.status == 500)
          updateErrorPopUp(errorState);

        if (response.status === 400) {
          response.json().then((responseJson) => {
            if(responseJson['status-description']){
              errorState.description = responseJson['status-description'];

              if(responseJson['status-code'] == 4053){
                errorState.title = t('reset_password_too_easy');
                errorState.btnBackText = t('retry_title');
              }

              updateErrorPopUp(errorState);
            }
          });
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <>
      <div
        style={{ backgroundImage: `url(${bgImage})` }}
        className="recover-password-container"
      >
        <div className="logo-container">
          <img
            alt="goalsetter icon"
            className="image-responsive recover-password-logo"
            src={gsIcon}
          />
        </div>
        <div className="containers">
          <form
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
            onSubmit={handleSubmit(onSubmit)}
          >
            <div className="auth-modal">
              <div className="auth-modal-content">
                <div className="auth-modal-content recover-password">

                </div>
                <div className="auth-modal-content-heading">
                  <h2 id="pageTitle">{t('reset_password_title')}</h2>
                </div>
                <div className="auth-modal-content">
                  <div className="auth-modal-content-form-container">
                    <div className="auth-modal-content-form-group">
                      <label
                        className="auth-modal-content-form-group-label"
                        htmlFor="password"
                      >
                        {t('reset_password_new')}
                      </label>
                      <div className="auth-modal-content-form-group-input">
                        <input
                          onChange={onChangeInput}
                          type="password"
                          name="password"
                          className="bigger-input"
                          ref={register({
                            required: true,
                            validate: { format: isValidPassword },
                          })}
                        />
                      </div>
                    </div>
                    <div className="auth-modal-content-form-group">
                      <label
                        className="auth-modal-content-form-group-label"
                        htmlFor="passwordRepeat"
                      >
                        {t('reset_password_repeat')}
                      </label>
                      <div className="auth-modal-content-form-group-input">
                        <input
                          onChange={onChangeInput}
                          type="password"
                          name="passwordRepeat"
                          className="bigger-input"
                          ref={register({
                            required: true,
                            validate: {
                              match: passwordsMatches,
                              format: isValidPassword,
                            },
                          })}
                        />
                      </div>
                    </div>
                    {
                      getErrorMessage() != null &&    
                      <div className="auth-modal-content-form-group">
                        {showErrors()}
                      </div>
                    }
                    <div className="auth-modal-content-form-group">
                      <LoadingButton
                        text={t('reset_password_reset')}
                        loading={loading}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>

      <ErrorPopUp
        description={errorPopUp.description}
        show={errorPopUp.show}
        title={errorPopUp.title}
        btnBackText={errorPopUp.btnBackText} 
        onBack={() => updateErrorPopUp({ ...errorPopUp, show: false })}
      />
    </>
  );
};

export default ChangePasswordView;
